import _ from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {buildTeammatePath} from '../../../../helpers/modelHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {MessageModel} from '../../../../models/api/messageModel';
import {TeammatesFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
} from '../../../../services/tracking';
import {ResourceMap} from '../../../../store/appTypes';
import {getMessages} from '../../../../store/data/dataSelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getShouldAlphabetizeWorkspace} from '../../../../store/data/preferences/preferencesSelectors';
import {getUserStarredTagId} from '../../../../store/data/tags/tagsSelectors';
import {getUserId, getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {
  buildTeammateWorkspaceExpandedKey,
  makeGetIsTeammateWorkspaceExpanded,
} from '../../../../store/data/workspace/workspaceExpandedSelectors';
import {isMessageInOutbox} from '../../../../store/panels/cells/helpers/cellsPanelHelpers';
import {getShouldSimplifyPersonalWorkspace} from '../../../../store/stateless/cellsEmptySetupActionsSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {defaultIsTeammateWorkspaceExpanded} from './constants';
import {TeammateWorkspace} from './teammateWorkspace';

/*
 * Selectors.
 */

const getIsOutboxEmpty = createSelector(
  [getUserId, getMessages],
  (userId, messages) => !someMessageInOutbox(userId, messages),
);

function someMessageInOutbox(userId: number, messages: ResourceMap<MessageModel>): boolean {
  return _(messages).some((message) => Boolean(message && isMessageInOutbox(userId, message)));
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<{}>()(() => ({
  userId: getUserId,
  userNamespace: getUserNamespace,
  userStarredTagId: getUserStarredTagId,
  isExpanded: makeGetIsTeammateWorkspaceExpanded(),
  shouldHideDraftsAndSent: getShouldSimplifyPersonalWorkspace,
  isOutboxEmpty: getIsOutboxEmpty,
  shouldAlphabetizeWorkspace: getShouldAlphabetizeWorkspace,
}));

export const TeammateWorkspaceContainer = connect(
  mapStateToProps,
  {toggleExpandItemAction, navigateToLinkAction},
  (stateProps, dispatchProps, ownProps) => {
    const {userNamespace, userId} = stateProps;

    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.PERSONAL_SECTION,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItemAction(
        buildTeammateWorkspaceExpandedKey(userNamespace),
        defaultIsTeammateWorkspaceExpanded,
      );
    };

    const onNavigateToInboxClick = () => {
      dispatchProps.navigateToLinkAction(buildTeammatePath(userId, TeammatesFoldersEnum.INBOX));
    };

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
      onNavigateToInboxClick,
    };
  },
)(TeammateWorkspace);
